import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchBatches,
  deleteBatch,
  transitionBatchStatus,
  fetchCourseById,
} from "./adminCourseService";
import "./adminCourses.css";

/* ── status config ── */
const STATUS_BADGE = {
  DRAFT:       "ac-badge-gray",
  OPEN:        "ac-badge-green",
  FULL:        "ac-badge-orange",
  IN_PROGRESS: "ac-badge-blue",
  COMPLETED:   "ac-badge-purple",
  CANCELLED:   "ac-badge-red",
};

/* next allowed transitions per status */
const TRANSITIONS = {
  DRAFT:       [{ to: "OPEN",        label: "Open" }],
  OPEN:        [{ to: "IN_PROGRESS", label: "Start" }, { to: "CANCELLED", label: "Cancel" }],
  FULL:        [{ to: "IN_PROGRESS", label: "Start" }, { to: "CANCELLED", label: "Cancel" }],
  IN_PROGRESS: [{ to: "COMPLETED",   label: "Complete" }, { to: "CANCELLED", label: "Cancel" }],
  COMPLETED:   [],
  CANCELLED:   [],
};

const TRANSITION_BTN = {
  OPEN:        "ac-btn-success",
  IN_PROGRESS: "ac-btn-primary",
  COMPLETED:   "ac-btn-purple",
  CANCELLED:   "ac-btn-danger",
};

const MODE_BADGE = { BATCH: "ac-badge-blue", PERSONAL: "ac-badge-purple" };
const MODE_LABEL = { BATCH: "Group", PERSONAL: "Personal" };

const AdminBatchListPage = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [courseName, setCourseName] = useState("");
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transitioning, setTransitioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [courseRes, batchRes] = await Promise.all([
        fetchCourseById(courseId),
        fetchBatches(courseId),
      ]);
      setCourseName(courseRes.data?.name || "");
      setBatches(batchRes.data || []);
    } catch {
      setError("Failed to load batches.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const handleTransition = async (batch, to) => {
    if (!window.confirm(`Move "${batch.batchName}" to ${to}?`)) return;
    setTransitioning(batch.id);
    setError("");
    try {
      await transitionBatchStatus(courseId, batch.id, to);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Transition failed.";
      setError(String(msg));
    } finally {
      setTransitioning(null);
    }
  };

  const handleDelete = async (batch) => {
    if (!window.confirm(`Delete batch "${batch.batchName}"? This cannot be undone.`)) return;
    setError("");
    try {
      await deleteBatch(courseId, batch.id);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Delete failed.";
      setError(String(msg));
    }
  };

  if (loading) return <div className="ac-loading">Loading batches…</div>;

  return (
    <div>
      <div className="ac-header">
        <div>
          <h2>Batches</h2>
          <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "0.2rem" }}>
            {courseName}
          </div>
        </div>
        <div className="ac-actions">
          <button
            className="ac-btn ac-btn-primary"
            onClick={() => navigate(`/admin/courses/${courseId}/batches/new`)}
          >
            + Create Batch
          </button>
          <button
            className="ac-btn ac-btn-ghost"
            onClick={() => navigate("/admin/courses")}
          >
            ← Courses
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: "#fee2e2", color: "#b91c1c",
          padding: "0.6rem 0.9rem", borderRadius: "6px",
          marginBottom: "1rem", fontSize: "0.88rem",
        }}>
          {error}
        </div>
      )}

      {batches.length === 0 && (
        <div className="ac-empty">No batches yet. Click "+ Create Batch" to add one.</div>
      )}

      {batches.length > 0 && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Batch Name</th>
                <th>Mode</th>
                <th>Start Date</th>
                <th>Time</th>
                <th>Instructor</th>
                <th>Capacity</th>
                <th>Enrolled</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id}>
                  <td><strong>{b.batchName}</strong></td>
                  <td>
                    <span className={`ac-badge ${MODE_BADGE[b.mode] || "ac-badge-gray"}`}>
                      {MODE_LABEL[b.mode] || b.mode}
                    </span>
                  </td>
                  <td>{b.startDate || "—"}</td>
                  <td>
                    {b.dailySessionTime
                      ? `${b.dailySessionTime} ${b.timezone || ""}`
                      : "—"}
                  </td>
                  <td>{b.instructorName || "—"}</td>
                  <td>
                    {b.mode === "PERSONAL"
                      ? "1"
                      : `${b.minCapacity ?? "—"} – ${b.maxCapacity ?? "—"}`}
                  </td>
                  <td>{b.enrolledCount ?? 0}</td>
                  <td>
                    <span className={`ac-badge ${STATUS_BADGE[b.status] || "ac-badge-gray"}`}>
                      {b.status}
                    </span>
                  </td>
                  <td>
                    <div className="ac-actions">
                      {/* status transitions */}
                      {(TRANSITIONS[b.status] || []).map(({ to, label }) => (
                        <button
                          key={to}
                          className={`ac-btn ac-btn-sm ${TRANSITION_BTN[to] || "ac-btn-ghost"}`}
                          disabled={transitioning === b.id}
                          onClick={() => handleTransition(b, to)}
                        >
                          {label}
                        </button>
                      ))}

                      <button
                        className="ac-btn ac-btn-ghost ac-btn-sm"
                        onClick={() =>
                          navigate(`/admin/courses/${courseId}/batches/${b.id}/edit`)
                        }
                      >
                        Edit
                      </button>

                      {/* view detail */}
                      <button
                        className="ac-btn ac-btn-primary ac-btn-sm"
                        onClick={() =>
                          navigate(`/admin/courses/${courseId}/batches/${b.id}`)
                        }
                      >
                        View
                      </button>

                      {/* delete — only DRAFT or CANCELLED */}
                      {(b.status === "DRAFT" || b.status === "CANCELLED") && (
                        <button
                          className="ac-btn ac-btn-danger ac-btn-sm"
                          onClick={() => handleDelete(b)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBatchListPage;
